import React from "react";
import LazyImage from "../../ui/LazyImage";
import { GripVertical, Plus, PenLine, Keyboard, X } from "lucide-react";

const apiURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const ChoiceRowItem = ({
    choice,
    idx,
    type,
    colors,
    uploadingIdx,
    draggedChoiceIdx,
    inputStyle,
    onSetChoice,
    onImageClick,
    onDragStart,
    onDragEnd,
    onDrop,
    onRemove,
    showRemove,
}) => (
    <div
        className="d-flex align-items-center gap-2 px-3 py-2"
        draggable
        onDragStart={() => onDragStart(idx)}
        onDragOver={(e) => e.preventDefault()}
        onDragEnd={onDragEnd}
        onDrop={() => onDrop(idx)}
        style={{
            border: `1px solid ${colors?.borderColor || '#E9E4DB'}`,
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            opacity: draggedChoiceIdx === idx ? 0.3 : 1,
            cursor: 'grab'
        }}
    >
        <div style={{ color: '#D0C8BF', cursor: 'grab' }}>
            <GripVertical size={18} />
        </div>
        <span style={{ color: '#8B7D6B', fontSize: '0.68rem', fontWeight: '800', width: '18px', flexShrink: 0, textAlign: 'center' }}>#{idx + 1}</span>

        {(type === 'image-select' || type === 'image-choice') && (
            <div
                style={{ position: 'relative', flexShrink: 0, width: '44px', height: '44px', cursor: 'pointer' }}
                onClick={() => onImageClick(idx)}
                title={choice.imageUrl ? 'Click to change image' : 'Click to upload image'}
            >
                {choice.imageUrl ? (
                    <LazyImage
                        src={choice.imageUrl.startsWith('http') ? choice.imageUrl : `${apiURL}${choice.imageUrl}`}
                        alt={choice.label}
                        style={{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: '8px', border: `1px solid ${colors?.borderColor || '#E9E4DB'}`, display: 'block', backgroundColor: '#F7F3EE', padding: '2px' }}
                    />
                ) : (
                    <div style={{ width: '44px', height: '44px', borderRadius: '8px', border: '1.5px dashed #C5A075', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(197, 160, 117, 0.08)' }}>
                        {uploadingIdx === idx
                            ? <span className="spinner-border spinner-border-sm" style={{ color: '#C5A075' }}></span>
                            : <Plus size={16} color="#C5A075" />
                        }
                    </div>
                )}
                {choice.imageUrl && (
                    <div
                        style={{ position: 'absolute', inset: 0, borderRadius: '8px', background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0}
                    >
                        <PenLine size={13} color="#fff" />
                    </div>
                )}
            </div>
        )}

        {type === 'color-picker' && (
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <div style={{ position: 'relative', width: '44px', height: '44px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${colors?.borderColor || '#E9E4DB'}` }} title="Pick Color">
                    <input
                        type="color"
                        value={choice.colorCode?.length === 7 ? choice.colorCode : '#000000'}
                        onChange={e => onSetChoice(idx, 'colorCode', e.target.value.toUpperCase())}
                        style={{ width: '56px', height: '56px', padding: 0, border: 'none', margin: '-6px', cursor: 'pointer', background: 'transparent' }}
                    />
                </div>
                <input
                    style={{ ...inputStyle, fontSize: '0.8rem', padding: '7px 8px', width: '85px', flex: 'none', fontFamily: 'monospace' }}
                    placeholder="#Hex"
                    value={choice.colorCode || ''}
                    onChange={e => onSetChoice(idx, 'colorCode', e.target.value)}
                    onBlur={e => {
                        let val = e.target.value.trim();
                        if (!val) return;
                        if (!val.startsWith('#')) val = '#' + val;
                        if (val.length > 1 && val.length < 7) {
                            if (val.length !== 4) val = val.padEnd(7, val.charAt(val.length - 1));
                        }
                        if (/^#[0-9A-F]{6}$/i.test(val) || /^#[0-9A-F]{3}$/i.test(val)) {
                            onSetChoice(idx, 'colorCode', val.toUpperCase());
                        }
                    }}
                />
            </div>
        )}

        <div className="d-flex flex-column gap-1" style={{ flex: '5 1 0' }}>
            <div className="d-flex gap-2">
                <input
                    style={{ ...inputStyle, fontSize: '0.85rem', padding: '7px 11px', flex: '2 1 0' }}
                    placeholder="Label (display)"
                    value={choice.label}
                    onChange={e => onSetChoice(idx, 'label', e.target.value)}
                />
                <input
                    style={{ ...inputStyle, fontSize: '0.85rem', padding: '7px 11px', flex: '3 1 0' }}
                    placeholder="Tooltip text"
                    value={choice.tooltip || ''}
                    onChange={e => onSetChoice(idx, 'tooltip', e.target.value)}
                />
            </div>
            {choice.hasCustomInput && (
                <input
                    style={{ ...inputStyle, fontSize: '0.8rem', padding: '5px 10px' }}
                    placeholder="Input placeholder (e.g. Enter your value...)"
                    value={choice.customInputPlaceholder || ''}
                    onChange={e => onSetChoice(idx, 'customInputPlaceholder', e.target.value)}
                />
            )}
        </div>
        <input type="hidden" value={choice.value} readOnly />

        <button
            type="button"
            title={choice.hasCustomInput ? 'Disable custom text input' : 'Enable custom text input for this choice'}
            onClick={() => onSetChoice(idx, 'hasCustomInput', !choice.hasCustomInput)}
            style={{ background: choice.hasCustomInput ? 'rgba(99,102,241,0.12)' : 'rgba(0,0,0,0.04)', border: `1px solid ${choice.hasCustomInput ? '#6366f1' : '#D1D5DB'}`, borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', flexShrink: 0, color: choice.hasCustomInput ? '#6366f1' : '#9CA3AF', transition: 'all 0.15s' }}
        >
            <Keyboard size={13} />
        </button>

        {showRemove && (
            <button
                type="button"
                className="btn btn-sm flex-shrink-0"
                onClick={() => onRemove(idx)}
                style={{ border: 'none', backgroundColor: '#FEF2F2', color: '#ef4444', borderRadius: '8px', padding: '7px 9px' }}
            >
                <X size={12} />
            </button>
        )}
    </div>
);

export default ChoiceRowItem;
